<template>
  <div class="wrapper">
    <ItemSelectionDialog
      :is-active="isItemSelectionDialogActive"
      :instruction="selectedInstruction"
      @onClose="onItemSelectionClose"
      @onNewItem="onNewItem"
      @onUpdateItem="onUpdateItem"
      @onDeleteItem="onDeleteItem"
    />
    <b-button
      expanded
      :disabled="isTaskCompleted"
      type="is-light"
      @click="openSelectInstruction"
      class="create-new"
    >
      <div class="btn-center-content">
        <b-icon icon="plus" size="fa-2x"> </b-icon> Create new
      </div>
    </b-button>

    <div class="items-container">
      <div
        v-for="(instruction, index) in taskQuestions"
        :class="{ 'child-question': instruction.parentId }"
        @click="openInstruction(instruction)"
        :id="'instruction_' + instruction._id"
        :key="instruction._id"
        :draggable="isTaskCompleted ? false : true"
        @dragstart="dragStart($event, instruction._id)"
        @dragover.prevent
        @drop="drop($event, instruction._id)"
        @dragenter.prevent="dragEnter($event, instruction._id)"
        @dragleave.prevent="dragLeave($event, instruction._id)"
      >
        <div class="instruction full">
          <div class="instruction-title">
            {{ instruction.label }}.
            <span v-if="instruction.subLabel">{{ instruction.subLabel }} </span>
            <span>{{ instruction.question }} </span>
            <div v-if="instruction.isPictureMandatory" class="req-picture">
              Picture required.
            </div>
          </div>

          <div class="image-container-helper" v-if="instruction.imageReference">
            <img :src="getImageReference(instruction)" alt="image" />
          </div>

          <div style="display: flex; flex-direction: row; width: 100%">
            <div class="question-details" v-if="instruction.type === 'action'">
              Action question
            </div>
            <div class="question-details" v-if="instruction.type === 'yesno'">
              Yes/No
            </div>
            <div
              class="question-details"
              v-if="instruction.type === 'textnumber'"
            >
              Text or Number
            </div>
            <div
              v-if="
                instruction.type === 'multiplechoice' ||
                instruction.type === 'singlechoice'
              "
              class="question-details"
            >
              <!-- Multiple choice options rendering -->
              <span
                v-for="(option, optionIndex) in instruction.multipleChoices"
                :key="optionIndex"
                class="option"
              >
                {{ String.fromCharCode(65 + optionIndex) }}.
                <template v-if="!option.other">{{
                  option.text?.defaultMC || option?.text || option.defaultMC
                }}</template>
                <template v-else>Other</template>
              </span>
            </div>
            <div
              v-if="['tablequestion', 'importcsv'].includes(instruction.type)"
              class="question-details"
            >
              <div class="table-description">
                Table {{ instruction.dimensions }}
              </div>

              <div
                v-if="['tablequestion', 'importcsv'].includes(instruction.type)"
                class="image-container"
              >
                <div v-if="getTableImage(instruction)">
                  <img :src="getTableImage(instruction)" alt="Table image" />
                </div>
                <div v-else>
                  <b-icon icon="image-area" size="is-medium"></b-icon>
                </div>
              </div>
              <TableQuestionLayout
                :rows="instruction.tableRow"
                :cols="instruction.tableColumn"
                :data="instruction.tableData"
                displayType="preview"
              />
            </div>
            <div
              v-if="instruction.type === 'smarttablequestion'"
              class="question-details"
            >
              <div class="table-description">
                Table {{ instruction.dimensions }}
              </div>

              <div
                v-if="instruction.type === 'smarttablequestion'"
                class="image-container"
              >
                <div v-if="getTableImage(instruction)">
                  <img :src="getTableImage(instruction)" alt="Table image" />
                </div>
                <div v-else>
                  <b-icon icon="image-area" size="is-medium"></b-icon>
                </div>
              </div>
              <SmartTableQuestionLayout
                :rows="instruction.tableRow"
                :cols="instruction.tableColumn"
                :data="instruction.tableData"
                displayType="preview"
              />
            </div>
          </div>
        </div>
        <div
          class="add-child"
          @click.stop="addSubInstruction(instruction._id)"
          v-if="!instruction.parentId"
        >
          <div class="btn-center-content">
            <b-icon icon="subdirectory-arrow-right" size="fa-2x"> </b-icon>
            Create new sub-instruction
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ItemSelectionDialog from './components/ItemSelectionDialog';
import DIJVueConfigurations from '../../../helpers/DIJVueConfigurations';
import ConflictDialog from '../../ConflictDialog';
import {
  CREATE_QUESTION,
  CREATE_QUESTIONS,
  DELETE_QUESTION,
  UPDATE_QUESTION,
  SAVE_QUESTIONS,
} from '../../../store/questions/actions/actionTypes';
import {
  GET_TASK_QUESTIONS,
  FETCH_QUESTION_LABEL,
} from '../../../store/questions/getters/getterTypes';

import {
  CHANGE_QUESTIONS_ORDER,
  QUESTIONS_ORDER,
} from '../../../store/questions/mutations/mutationTypes';
import TableQuestionLayout from './components/TableQuestionLayout';
import SmartTableQuestionLayout from './components/SmartTableQuestionLayout';
import { SET_LOADING_STATE } from '../../../store/loading/mutations/mutationTypes';
import OfflineMixin from '../../../mixins/OfflineMixin';

export default {
  components: {
    ItemSelectionDialog,
    TableQuestionLayout,
    SmartTableQuestionLayout,
    ConflictDialog,
  },
  mixins: [OfflineMixin],
  data() {
    return {
      isItemSelectionDialogActive: false,
      selectedInstruction: null,
      draggingIndex: -1, // Index of the item being dragged
      draggedElement: null, // Store the reference to the dragged element
      enteredElement: null,
      conflicts: [],
      conflictDialogVisible: false,
      parentId: null,
    };
  },
  props: {
    task: {
      type: Object,
      required: true,
    },
  },
  watch: {
    taskQuestions(newVal) {
      if (this.selectedInstruction && this.selectedInstruction._id) {
        const updated = newVal.find(
          (q) => q._id === this.selectedInstruction._id
        );
        if (updated) {
          this.selectedInstruction = { ...updated };
        }
      }
    },
  },
  computed: {
    taskQuestions() {
      return this.$store.getters[GET_TASK_QUESTIONS];
    },
    isTaskCompleted() {
      return this.task.workStatusCode
        ? this.task.workStatusCode.toString() === '2'
        : false;
    },
    user() {
      return this.$store.state.currentUser;
    },
  },
  methods: {
    sortMultipleChoices(options) {
      return options.sort((a, b) => (a.other ? 1 : 0) - (b.other ? 1 : 0));
    },
    onResolved(resolvedEntities) {
      resolvedEntities.forEach((resolvedEntity) => {
        this.$store.dispatch(UPDATE_QUESTION, resolvedEntity);
      });

      this.conflicts = [];
      this.onCancel();
    },
    onCancel() {
      this.conflictDialogVisible = false;
    },
    dragStart(event, index) {
      this.draggingIndex = index;
      this.draggedElement = event.target;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', this.draggedElement.outerHTML);
      this.draggedElement.style.opacity = '0.4';
    },
    dragEnter(event, index) {
      const targetInstruction = this.taskQuestions.find((q) => q._id === index);
      const fromInstruction = this.taskQuestions.find(
        (q) => q._id === this.draggingIndex
      );
      this.enteredElement = event.target;
      const targetElement = event.target.closest('.instruction'); // Ensure this targets the .instruction element

      if (
        fromInstruction.parentId &&
        targetInstruction.parentId !== fromInstruction.parentId &&
        targetElement
      ) {
        targetElement.classList.add('disabled-drop');
        return;
      }

      if (
        !fromInstruction.parentId &&
        targetInstruction.parentId &&
        targetElement
      ) {
        targetElement.classList.add('disabled-drop');
        return;
      }

      if (this.draggingIndex !== index && targetElement) {
        targetElement.classList.add('shake');
      }
    },
    dragLeave(event) {
      if (!event.target.classList.contains('instruction')) {
        return;
      }

      if (
        this.enteredElement &&
        this.enteredElement.parentNode.classList.contains('instruction')
      ) {
        return;
      }
      event.target.classList.remove('shake');
      event.target.classList.remove('disabled-drop');
    },
    drop(event, index) {
      event.preventDefault();
      const targetElement = event.target.closest('.instruction');
      if (targetElement) {
        targetElement.classList.remove('shake');
        targetElement.classList.remove('disabled-drop');
      }
      this.draggedElement.style.opacity = '1'; // Reset the opacity of the dragged element
      event.target.style.background = ''; // Reset background of the drop target
      this.moveItem(this.draggingIndex, index);
      this.draggingIndex = -1; // Reset dragging index
      this.draggedElement = null; // Clear the reference to the dragged element
    },
    moveItem(from, to) {
      this.$store.commit(CHANGE_QUESTIONS_ORDER, {
        question: from,
        targetQuestion: to,
      });

      this.$store.dispatch(SAVE_QUESTIONS, {
        taskId: this.task._id,
        data: this.taskQuestions,
      });
    },
    getTableImage(instruction) {
      // For local env we need to point to the local s3
      if (instruction.tableImg && window.location.host.includes('localhost')) {
        return `http://localhost:4566/images${instruction.tableImg}`;
      }

      return instruction.tableImg;
    },
    openInstruction(instruction) {
      if (this.isTaskCompleted) {
        this.$buefy.toast.open({
          duration: 5000,
          message: this.$t('task_completed_message'),
          type: 'is-warning',
        });
        return;
      }

      this.selectedInstruction = instruction;
      this.isItemSelectionDialogActive = true;
    },
    async onNewItem(itemData) {
      let parentLabel;
      let parentQuestion;
      if (this.parentId) {
        itemData.parentId = this.parentId;
        parentQuestion = this.taskQuestions.find(
          (q) => q._id === this.parentId
        );
        parentLabel = parentQuestion.label;
        this.parentId = null;
      }

      const newQuestion = {
        taskId: this.task._id,
        question: {
          ...itemData,
          label: parentLabel || this.$store.getters[FETCH_QUESTION_LABEL],
          subLabel: parentLabel && this.getSubLabel(parentQuestion),
        },
      };
      const response = await this.$store.dispatch(CREATE_QUESTION, newQuestion);

      if (itemData.file) {
        this.$store.commit(SET_LOADING_STATE, { value: true, root: true });
        itemData._id = response._id;
        await this.uploadImageReference(itemData);
        this.$store.commit(SET_LOADING_STATE, { value: false, root: true });
      }

      this.isItemSelectionDialogActive = false;
    },
    getSubLabel(parentQuestion) {
      const subLabels = this.taskQuestions.filter(
        (q) => q.parentId === parentQuestion._id
      );
      let maxSubLabel = 0;
      subLabels.forEach((q) => {
        const labelNum = parseInt(q.subLabel, 10);
        if (!isNaN(labelNum) && labelNum > maxSubLabel) {
          maxSubLabel = labelNum;
        }
      });
      return (maxSubLabel + 1).toString();
    },
    async onDeleteItem(itemData) {
      await this.$store.dispatch(DELETE_QUESTION, itemData._id);
      this.$store.commit(QUESTIONS_ORDER);
      if (itemData.imageReference) {
        if (itemData.imageReference === this.task.thumbnailUrl) {
          const currentTask = this.task;
          this.$store.dispatch('updateTask', {
            ...currentTask,
            thumbnailUrl: null,
          });
        }
      }
      if (itemData.tableImg) {
        if (itemData.tableImg === this.task.thumbnailUrl) {
          const currentTask = this.task;
          this.$store.dispatch('updateTask', {
            ...currentTask,
            thumbnailUrl: null,
          });
        }
      }
      const questionLength = this.$store.state.QuestionsModule.questions.length;
      if (questionLength > 0) {
        this.$store.dispatch(SAVE_QUESTIONS, {
          taskId: this.task._id,
          data: this.taskQuestions,
        });
      }
      this.isItemSelectionDialogActive = false;
    },
    async onUpdateItem(itemData) {
      if (itemData.file) {
        await this.uploadImageReference(itemData);
      } else {
        console.log({ itemData });
        await this.$store.dispatch(UPDATE_QUESTION, itemData);
      }

      this.isItemSelectionDialogActive = false;
    },

    async uploadImageReference(itemData) {
      const { file, _id } = itemData;
      const site = this.user.location;
      const path = `uploads/sites/${site}/${new Date().getFullYear()}/${
        this.task._id
      }/question/${_id}`;
      const url = await this.uploadToS3(file, path);
      await this.waitForSeconds(5);
      await this.$store.dispatch(UPDATE_QUESTION, {
        ...itemData,
        _id,
        imageReference: url,
      });
      const currentTask = this.task;

      if (itemData.useImageReference) {
        this.$store.dispatch('updateTask', {
          ...currentTask,
          thumbnailUrl: url,
        });
      }
    },

    async uploadToS3(file, path) {
      const dijConfigurations = new DIJVueConfigurations();
      const url = await dijConfigurations.uploadFileToS3(file, file.name, path);
      return url;
    },

    getImageReference(instruction) {
      // For local env we need to point to the local s3
      if (
        instruction.imageReference &&
        window.location.host.includes('localhost')
      ) {
        return `http://localhost:4566/images${instruction.imageReference}`;
      }
      return instruction.imageReference;
    },

    openSelectInstruction() {
      this.selectedInstruction = null;
      this.isItemSelectionDialogActive = true;
    },
    addSubInstruction(parentId) {
      this.selectedInstruction = null;
      this.isItemSelectionDialogActive = true;
      this.parentId = parentId;
    },
    onItemSelectionClose() {
      this.isItemSelectionDialogActive = false;
    },
    async forceSave() {
      await this.$store.dispatch(CREATE_QUESTIONS, {
        taskId: this.task._id,
        questions: this.taskQuestions,
      });
    },
    waitForSeconds(seconds) {
      return new Promise((resolve) => {
        setTimeout(resolve, seconds * 1000);
      });
    },
  },
};
</script>

<style scoped>
@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-10px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(10px);
  }
}

.shake {
  animation: shake 2s cubic-bezier(0.36, 0.07, 0.19, 0.97) both infinite;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  perspective: 1000px;
}

.disabled-drop {
  cursor: no-drop;
  opacity: 0.4;
}

.instruction.full.drag-over {
  background-color: #f0f0f0; /* Temporary background color when dragging over */
}
.add-child {
  margin-left: 30px;
  border-bottom-style: dashed;
  border-left-style: dashed;
  border-right-style: dashed;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  border-color: grey;
  padding: 5px;
  border-width: 2px;
}
.create-new {
  margin-bottom: 20px;
  min-height: 75px;
  font-size: larger;
  font-weight: bold;
  background: #f5f5f5;
  padding: 10px;
  border-radius: 10px;
}

button span {
  width: 100%;
}

.full-width {
  width: 100%;
}

.subtitle {
  display: flex;
  flex-direction: row;
}

.child-question {
  margin-left: 30px;
}

.table-description {
  align-items: start;
  display: flex;
  width: 100%;
}

.question-details {
  font-size: large;
  color: gray;
  width: 100%;
}

.req-picture {
  margin-left: auto;
  color: gray;
  font-style: italic;
  font-size: medium;
  display: flex;
  align-items: center;
}

.items-container {
  gap: 10px;
  display: flex;
  flex-direction: column;
  overflow: scroll;
  overflow-x: hidden;
  min-height: calc(100vh - 245px);
  max-height: calc(100vh - 245px);
  padding: 0 10px;
}

.image-container-helper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  img {
    max-height: 250px;
  }
}

@media only screen and (max-device-width: 1600px) and (orientation: portrait),
  only screen and (max-device-width: 1600px) and (orientation: landscape) {
  .items-container {
    gap: 10px;
    display: flex;
    flex-direction: column;
    overflow: scroll;
    overflow-x: hidden;
    min-height: calc(100vh - 300px);
    max-height: calc(100vh - 300px);
    padding: 0 10px;
  }
}

.instruction {
  font-size: larger;
  font-weight: bold;
  background: #f5f5f5;
  padding: 10px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
}

.instruction:hover {
  background: #f5f5f5a6;
}

.instruction-title {
  display: flex;
  flex-direction: row;
  width: 100%;
  font-size: larger;

  span {
    white-space: pre-wrap;
    text-align: left;
  }
}

.instruction:hover {
  cursor: pointer;
}

.justify-start {
  justify-content: start;
}

.wrapper {
  gap: 20px;
  padding: 20px 10px;
  text-align: center;
  justify-content: center;
  position: absolute;
  height: 100%;
  width: 100%;
  background-color: #dddddd;
}

.btn-center-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding-right: 25px;
  cursor: pointer;
}

.btn-center-content-column {
  position: absolute;
  top: 10%;
  padding-right: 30px;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  gap: 5px;
}

.option {
  margin-right: 10px; /* Adjust spacing between options as needed */
  font-size: x-large;
  font-weight: 600;
  color: gray;
}

.description {
  font-size: medium;
  font-weight: 600;
  color: gray;
}

.image-container {
  margin-bottom: 10px;
  width: 100%;
}
</style>
